import { on } from "@ngrx/store";
import { ContractAdminState } from "../contract-admin.state";
import { CatalogDatasetsActions } from "./catalog-datasets.actions";
import { PagesStore } from "../../../pages.state";

// **************************************************************************
// HELPER: Update contractAdmin state within PagesStore
// **************************************************************************

function withContractAdmin(state: PagesStore, updater: (contractAdmin: ContractAdminState) => ContractAdminState): PagesStore {
    return { ...state, contractAdmin: updater(state.contractAdmin) };
}

// **************************************************************************
// CATALOG DATASETS REDUCER
// Handles operation states only - data is in application/data store
// **************************************************************************

// *** ECO LABELS OPERATIONS ***
const createEcoLabel = on(CatalogDatasetsActions.createEcoLabel, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: true },
    },
})));

const createEcoLabelSuccess = on(CatalogDatasetsActions.createEcoLabelSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error: null,
})));

const createEcoLabelFailure = on(CatalogDatasetsActions.createEcoLabelFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error,
})));

const updateEcoLabel = on(CatalogDatasetsActions.updateEcoLabel, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: true },
    },
})));

const updateEcoLabelSuccess = on(CatalogDatasetsActions.updateEcoLabelSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error: null,
})));

const updateEcoLabelFailure = on(CatalogDatasetsActions.updateEcoLabelFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error,
})));

const deleteEcoLabel = on(CatalogDatasetsActions.deleteEcoLabel, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: true },
    },
})));

const deleteEcoLabelSuccess = on(CatalogDatasetsActions.deleteEcoLabelSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error: null,
})));

const deleteEcoLabelFailure = on(CatalogDatasetsActions.deleteEcoLabelFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error,
})));

const uploadEcoLabelImage = on(CatalogDatasetsActions.uploadEcoLabelImage, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: true },
    },
})));

const uploadEcoLabelImageSuccess = on(CatalogDatasetsActions.uploadEcoLabelImageSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error: null,
})));

const uploadEcoLabelImageFailure = on(CatalogDatasetsActions.uploadEcoLabelImageFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error,
})));

const deleteEcoLabelImage = on(CatalogDatasetsActions.deleteEcoLabelImage, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: true },
    },
})));

const deleteEcoLabelImageSuccess = on(CatalogDatasetsActions.deleteEcoLabelImageSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error: null,
})));

const deleteEcoLabelImageFailure = on(CatalogDatasetsActions.deleteEcoLabelImageFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, ecoLabels: false },
    },
    error,
})));

// *** UNSPSC OPERATIONS ***
const createUnspsc = on(CatalogDatasetsActions.createUnspsc, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: true },
    },
})));

const createUnspscSuccess = on(CatalogDatasetsActions.createUnspscSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error: null,
})));

const createUnspscFailure = on(CatalogDatasetsActions.createUnspscFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error,
})));

const updateUnspsc = on(CatalogDatasetsActions.updateUnspsc, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: true },
    },
})));

const updateUnspscSuccess = on(CatalogDatasetsActions.updateUnspscSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error: null,
})));

const updateUnspscFailure = on(CatalogDatasetsActions.updateUnspscFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error,
})));

const deleteUnspsc = on(CatalogDatasetsActions.deleteUnspsc, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: true },
    },
})));

const deleteUnspscSuccess = on(CatalogDatasetsActions.deleteUnspscSuccess, (state: PagesStore) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error: null,
})));

const deleteUnspscFailure = on(CatalogDatasetsActions.deleteUnspscFailure, (state: PagesStore, { error }) => withContractAdmin(state, contractAdminState => ({
    ...contractAdminState,
    catalogDatasets: {
        ...contractAdminState.catalogDatasets,
        loading: { ...contractAdminState.catalogDatasets.loading, unspsc: false },
    },
    error,
})));

export const catalogDatasetsReducer = [
    createEcoLabel,
    createEcoLabelSuccess,
    createEcoLabelFailure,
    updateEcoLabel,
    updateEcoLabelSuccess,
    updateEcoLabelFailure,
    deleteEcoLabel,
    deleteEcoLabelSuccess,
    deleteEcoLabelFailure,
    uploadEcoLabelImage,
    uploadEcoLabelImageSuccess,
    uploadEcoLabelImageFailure,
    deleteEcoLabelImage,
    deleteEcoLabelImageSuccess,
    deleteEcoLabelImageFailure,
    createUnspsc,
    createUnspscSuccess,
    createUnspscFailure,
    updateUnspsc,
    updateUnspscSuccess,
    updateUnspscFailure,
    deleteUnspsc,
    deleteUnspscSuccess,
    deleteUnspscFailure,
];
